'use client';

import { useTranslation } from 'next-i18next';
import type { ComponentType } from 'react';
import { useMemo } from 'react';

import { SSO_PROVIDER_DISCORD, SSO_PROVIDER_GOOGLE } from '@/constants/sso-providers';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';

import DiscordSsoButton from './discord-sso-button';
import { GoogleOAuthScope } from './google-oauth-scope';
import GoogleSsoButton from './google-sso-button';
import type { SsoButtonProps, SsoProvider } from './types';

// The OAuth context travels with the button instead of wrapping the whole app in _app: that is what keeps
// Google's 97 KB gsi/client script off every page that has no sign-in on it. It sits inside this component
// rather than around the block below so it mounts only when the google provider is actually enabled and
// rendered — the early return for an empty provider list must keep meaning "no third-party script".
function GoogleSsoButtonWithOAuthScope(props: SsoButtonProps) {
  return (
    <GoogleOAuthScope>
      <GoogleSsoButton {...props} />
    </GoogleOAuthScope>
  );
}

const PROVIDER_COMPONENTS: Record<SsoProvider, ComponentType<SsoButtonProps>> = {
  google: GoogleSsoButtonWithOAuthScope,
  discord: DiscordSsoButton,
};

interface SsoProvidersProps extends SsoButtonProps {
  showDivider?: boolean;
  // Desktop entry (src/pages/auth/desktop.tsx): when the C++ client already picked a provider via
  // `?provider=`, render only that provider and auto-start it — the user isn't shown the buttons
  // again. Falls back to the full enabled list if the requested provider isn't enabled.
  autoStartProvider?: SsoProvider;
}

export default function SsoProviders({ onAuthStart, onAuthSuccess, onAuthError, desktopRedirectUri, showDivider = true, autoStartProvider, trialContext }: SsoProvidersProps) {
  const { t } = useTranslation('common');
  const { isEnabled: isGoogleEnabled } = useFeatureToggle({ key: SSO_PROVIDER_GOOGLE, autoFetch: true });
  const { isEnabled: isDiscordEnabled } = useFeatureToggle({ key: SSO_PROVIDER_DISCORD, autoFetch: true });

  // Defensive guard: mesmo com a flag ON, só habilita o provider se o Client ID correspondente
  // estiver configurado no build. Sem isso, a biblioteca OAuth crasha a página ao inicializar.
  const hasGoogleClientId = Boolean(process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID);
  const hasDiscordClientId = Boolean(process.env.NEXT_PUBLIC_DISCORD_CLIENT_ID);

  // Fail-closed: só renderiza o provider quando a flag retorna explicitamente true E a env está setada.
  // Ao adicionar um novo provider, chamar useFeatureToggle para sua flag e adicionar um push condicional.
  const enabledProviders = useMemo(() => {
    const enabled: SsoProvider[] = [];
    if (isGoogleEnabled && hasGoogleClientId) enabled.push('google');
    if (isDiscordEnabled && hasDiscordClientId) enabled.push('discord');
    return enabled;
  }, [isGoogleEnabled, hasGoogleClientId, isDiscordEnabled, hasDiscordClientId]);

  // When a provider is pre-selected and enabled, render only it (and auto-start it). If it isn't
  // enabled, fall back to the full enabled list so the flow never dead-ends.
  const providersToRender = autoStartProvider && enabledProviders.includes(autoStartProvider) ? [autoStartProvider] : enabledProviders;

  if (providersToRender.length === 0) return null;

  return (
    <div className='flex flex-col items-center gap-3'>
      <div className='flex w-full flex-col items-center gap-2'>
        {providersToRender.map((provider) => {
          const ProviderButton = PROVIDER_COMPONENTS[provider];
          return <ProviderButton key={provider} onAuthStart={onAuthStart} onAuthSuccess={onAuthSuccess} onAuthError={onAuthError} desktopRedirectUri={desktopRedirectUri} autoStart={provider === autoStartProvider} trialContext={trialContext} />;
        })}
      </div>
      {showDivider && (
        <div className='flex w-full items-center gap-3'>
          <span className='h-px flex-1 bg-[#515E6C]/55' />
          <span className='text-xs font-medium uppercase tracking-wide text-[#7C7C89]'>{t('login.or_continue_with_email')}</span>
          <span className='h-px flex-1 bg-[#515E6C]/55' />
        </div>
      )}
    </div>
  );
}
